import { eurosToCents } from '@/utils/money';
import type { CustomerSignatureMode, CustomerSignatureSettings } from '@/types';

export interface CustomerSignatureFormState {
  mode: CustomerSignatureMode;
  /** Euro string the user typed; empty means no threshold. */
  thresholdEuros: string;
  requireForConsumers: boolean;
  allowBillingPartySigner: boolean;
  allowDriverSigner: boolean;
  consentText: string;
  consentTextVersion: number;
  /** YYYY-MM-DD or empty. */
  activeFrom: string;
}

export const DEFAULT_CUSTOMER_SIGNATURE_FORM: CustomerSignatureFormState = {
  mode: 'never',
  thresholdEuros: '',
  requireForConsumers: false,
  allowBillingPartySigner: false,
  allowDriverSigner: false,
  consentText: '',
  consentTextVersion: 1,
  activeFrom: '',
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function formStateFromSettings(
  settings: CustomerSignatureSettings | null | undefined,
): CustomerSignatureFormState {
  const mode =
    settings?.mode === 'always' || settings?.mode === 'conditional' ? settings.mode : 'never';
  const version = settings?.consentTextVersion;
  const threshold = settings?.thresholdGrossCents;
  return {
    mode,
    thresholdEuros:
      typeof threshold === 'number' && Number.isInteger(threshold) && threshold >= 0
        ? (threshold / 100).toFixed(2)
        : '',
    requireForConsumers: settings?.requireForConsumers === true,
    allowBillingPartySigner: settings?.allowBillingPartySigner === true,
    allowDriverSigner: settings?.allowDriverSigner === true,
    consentText: settings?.consentText ?? '',
    consentTextVersion:
      typeof version === 'number' && Number.isInteger(version) && version >= 1 ? version : 1,
    activeFrom: typeof settings?.activeFrom === 'string' && ISO_DATE.test(settings.activeFrom)
      ? settings.activeFrom
      : '',
  };
}

export type CustomerSignaturePayloadError = 'thresholdInvalid';

export function buildCustomerSignaturePayload(
  form: CustomerSignatureFormState,
  originalConsentText: string,
): { ok: true; payload: CustomerSignatureSettings } | { ok: false; error: CustomerSignaturePayloadError } {
  const trimmedThreshold = form.thresholdEuros.trim();
  let thresholdGrossCents: number | undefined;
  if (trimmedThreshold.length > 0) {
    const cents = eurosToCents(trimmedThreshold);
    if (cents == null || cents < 0) {
      return { ok: false, error: 'thresholdInvalid' };
    }
    thresholdGrossCents = cents;
  }

  const consentChanged = form.consentText !== originalConsentText;
  const payload: CustomerSignatureSettings = {
    mode: form.mode,
    requireForConsumers: form.requireForConsumers,
    allowBillingPartySigner: form.allowBillingPartySigner,
    allowDriverSigner: form.allowDriverSigner,
    consentText: form.consentText,
    consentTextVersion: consentChanged ? form.consentTextVersion + 1 : form.consentTextVersion,
  };
  if (thresholdGrossCents !== undefined) {
    payload.thresholdGrossCents = thresholdGrossCents;
  }
  if (ISO_DATE.test(form.activeFrom)) {
    payload.activeFrom = form.activeFrom;
  }
  return { ok: true, payload };
}
